import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { flushSync } from 'react-dom';
import api from '../../api/axiosInstance';
import './sideNavbar.css';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import PeopleIcon from '@mui/icons-material/People';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import SchoolIcon from '@mui/icons-material/School';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import QuizIcon from '@mui/icons-material/Quiz';
import EventIcon from '@mui/icons-material/Event';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupsIcon from '@mui/icons-material/Groups';
import { clearAuthStorage } from '../../utils/authStorage';

// ────────────────────────────────────────────
// NAV_SECTIONS — grouped navigation structure
// ────────────────────────────────────────────

const NAV_SECTIONS = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, excludedRoles: ['Viewer', 'Astern Admin'] },
      { label: 'Leaderboard', path: '/rankings', icon: LeaderboardIcon, requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent', 'Referral'] },
      // A referrer's own page. Only they see it: for anyone who sells, their referrals
      // already appear in My Referral Report beside the rest of their work.
      // (user 2026-10-18)
      { label: 'My Referrals', path: '/referral/dashboard', icon: DashboardIcon, requiredRoles: ['Referral'] },
    ],
  },
  {
    // Its own section rather than a leaf buried in Resources: this is a place people
    // go to, not a document they look up, and the create/edit entries have to be
    // visible or nobody finds them. Called "Events" flat - a training session is an
    // event, so the longer name only added a word. (user 2026-10-03)
    // Directly under Overview: the dashboard, the leaderboard, then what is on this
    // week. It sat below Resources, which put a thing people check daily underneath
    // the documents they look up now and then. (user 2026-10-04)
    section: 'Events',
    items: [
      {
        label: 'Events',
        icon: EventIcon,
        children: [
          // Create, edit and delete all live INSIDE the list, so there are no separate
          // menu entries for them. (user 2026-10-03: "remove the two menu create and
          // edit event")
          { label: 'All Events', path: '/events' },
          // The noticeboard is where an event is announced, so it belongs beside the
          // events rather than up in Overview. Route and right are unchanged.
          { label: 'Noticeboard', path: '/settings/noticeboard', icon: CampaignIcon, requiredRoleRights: ['NoticeBoard'] },
        ],
      },
    ],
  },
  {
    section: 'Incentives',
    items: [
      {
        label: 'Incentives',
        icon: EmojiEventsIcon,
        excludedRoles: ['Viewer'],
        children: [
          { label: 'Campaigns', path: '/incentives', excludedRoles: ['Viewer'] },
          { label: 'Manage Campaigns', path: '/incentives/manage', requiredRoleRights: ['IncentiveManagement'] },
        ],
      },
    ],
  },
  {
    section: 'Sales',
    items: [
      {
        label: 'Sales',
        icon: ShoppingCartIcon,
        children: [
          { label: 'All Sales', path: '/sales', requiredRoleRights: ['AgentSalesList'] },
          { label: 'All Customer', path: '/sales/customers', requiredRoleRights: ['AgentSalesList'] },
          { label: 'Group Sales', path: '/sales/group', requiredRoleRights: ['GroupSalesList'] },
          // Who is booked to which customer, and whether the payment has kept up
          // with the dates. Scoped by the API to the caller's own sales.
          { label: 'All Scheduling', path: '/sales/scheduling', requiredRoleRights: ['AgentSalesList', 'AllSalesList', 'GroupSalesList'] },
          { label: 'Create Sale', path: '/sales/create', requiredRoleRights: ['CreateSale'] },
        ],
      },
      {
        label: 'Commission',
        icon: MonetizationOnIcon,
        children: [
          { label: 'Personal PSC', path: '/commissions/psc', requiredRoleRights: ['PersonalSalesCommission'], availabilityKey: 'hasPsc' },
          { label: 'Downline DOC', path: '/commissions/doc', requiredRoleRights: ['DownlineOverridingCommission'], availabilityKey: 'hasDoc' },
          { label: 'Team TOC', path: '/commissions/toc', requiredRoleRights: ['TeamOverridingCommission'], availabilityKey: 'hasToc' },
          { label: 'Manager MOC', path: '/commissions/moc', requiredRoleRights: ['ManagerOverridingCommission'], availabilityKey: 'hasMoc' },
          // Markup MUC — only appears once somebody has actually earned one, like the
          // rest of this menu. A mark-up is rare, so most agents will never see it.
          { label: 'Markup MUC', path: '/commissions/muc', requiredRoleRights: ['PersonalSalesCommission'], availabilityKey: 'hasMuc' },
          { label: 'Referral RFC', path: '/commissions/referral', requiredRoleRights: ['PersonalSalesCommission'], availabilityKey: 'hasRef' },
          { label: 'Commission Slips', path: '/commissions/mySlips', requiredRoleRights: ['AgentCommissionSlips'] },
          { label: 'My Incentives', path: '/commissions/incentives', requiredRoleRights: ['PersonalSalesCommission'] },
        ],
      },
    ],
  },
  {
    section: 'Accounting',
    items: [
      {
        label: 'Accounting',
        icon: AssessmentIcon,
        children: [
          { label: 'All Sales', path: '/accounting/sales', requiredRoleRights: ['AllSalesList'] },
          { label: 'All Customer', path: '/accounting/customers', requiredRoleRights: ['AllSalesList'] },
          { label: 'Astern Claims', path: '/accounting/asternClaims', requiredRoleRights: ['TotalCommission'] },
          { label: 'All Sales Comm', path: '/accounting/salesCommissions', requiredRoleRights: ['AllSalesCommList'] },
          { label: 'All MUC', path: '/accounting/allMuc', requiredRoleRights: ['AllSalesCommList'] },
          { label: 'All RFC', path: '/accounting/referralFees', requiredRoleRights: ['AllSalesCommList'] },
          { label: 'Webbey Overriding', path: '/accounting/webbeyCommissions', requiredRoleRights: ['WebbeyCommission'] },
          { label: 'Slips', path: '/accounting/slips', requiredRoleRights: ['CommissionSlipGenerator'] },
          { label: 'CP58 (Tax)', path: '/accounting/cp58', requiredRoleRights: ['Cp58Generator'] },
        ],
      },
      {
        label: 'Approvals',
        icon: FactCheckIcon,
        children: [
          { label: 'Pending Approvals', path: '/approvals', requiredRoleRights: ['PendingApprovals'] },
        ],
      },
    ],
  },
  {
    section: 'Resources',
    items: [
      {
        label: 'Pricing & Tools',
        icon: CalculateIcon,
        excludedRoles: ['Astern Admin'],
        children: [
          // Each tool carries its own right so access is managed in the Permission
          // Manager instead of being open to anyone with a login. (user 2026-09-27)
          { label: 'ATAP Pricing', path: '/resources/pricing', requiredRoleRights: ['AtapPricing'] },
          { label: 'Add-on', path: '/resources/addons', requiredRoleRights: ['AddonPricing'] },
          { label: 'RSC', path: '/tools/rsc', requiredRoleRights: ['RscTool'] },
          { label: 'CSC', path: '/tools/csc', requiredRoleRights: ['CscTool'] },
          { label: 'Bill Analysis', path: '/tools/bill', requiredRoleRights: ['BillTool'] },
          { label: 'Design Request', path: '/design-requests', requiredRoleRights: ['DesignRequestAccess', 'CreateSale'] },
        ],
      },
      {
        label: 'Resources',
        icon: FolderIcon,
        excludedRoles: ['Astern Admin'],
        children: [
          { label: 'SOP', path: '/resources/sop', requiredRoleRights: ['SopAccess'], availabilityKey: 'hasSop' },
          { label: 'Tutorials', path: '/tutorials', icon: SchoolIcon },
          { label: 'Quiz', path: '/quiz', icon: QuizIcon },
          { label: 'Documents', path: '/resources/documents' },
        ],
      },
    ],
  },
  {
    // Its own section rather than a leaf under Resources: asking for help is not a
    // document you look up, and burying it made it hard to find at the moment somebody
    // needed it. (user 2026-10-03: "support can be another tab")
    section: 'Support',
    items: [
      {
        label: 'Support',
        icon: SupportAgentIcon,
        children: [
          { label: 'My Tickets', path: '/support' },
          { label: 'All Tickets', path: '/support/all', requiredRoleRights: ['SupportDesk'] },
        ],
      },
    ],
  },
  {
    section: 'Reports',
    items: [
      {
        label: 'Reports',
        icon: SummarizeIcon,
        // Reports surface to earning roles + accountant/super admin.
        // Pure admin roles (Admin / Astern Admin / Group Admin) do NOT see
        // the Reports section at all — they're carved out deliberately so
        // the menu only shows up for people who actually have sales /
        // commissions / payouts to report on.
        // Admin is here only so the event activity report is reachable; every other
        // child carries its own role list, so Admin still sees nothing else in here.
        requiredRoles: ['Accountant', 'Super Admin', 'Admin', 'Leader', 'Sales Agent', 'In-house Agent'],
        children: [
          // Sales / Commission / Payout are scoped inside the page (Personal /
          // Group / All tabs based on the viewer's rights — see salesReport.jsx).
          { label: 'Sales Report',      path: '/reports/sales',      requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          { label: 'My Commission Report', path: '/reports/commission', requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          // Company-wide — Accountant / Super Admin only. Route-guarded too. (user 2026-08-26)
          { label: 'All Commission Report', path: '/reports/commission/all', requiredRoles: ['Accountant', 'Super Admin'] },
          { label: 'My Incentive Report', path: '/reports/incentive', requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          { label: 'All Incentive Report', path: '/reports/incentive/all', requiredRoles: ['Accountant', 'Super Admin'] },
          // An agent's own slice: referrals on sales THEY sold. An agent-borne fee comes out
          // of their own PSC, so whether it was paid is their business — but the referrer's
          // bank details are not, and the server never sends them. (user 2026-10-14)
          { label: 'My Referral Report', path: '/reports/referral/mine', requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          // The company-wide one stays with the other all-company reports.
          { label: 'Referral Report', path: '/reports/referral', requiredRoles: ['Accountant', 'Super Admin'] },
          { label: 'Payout Report',     path: '/reports/payout',     requiredRoles: ['Accountant', 'Super Admin'] },
          // Webbey + Agent reports stay accountant/super-admin only — they're
          // org-wide views that don't make sense for an individual agent.
          { label: 'Agent Report',      path: '/reports/agents',     requiredRoles: ['Accountant', 'Super Admin'] },
          // Event attendance sits at the FOOT of the list - it is the newest report and
          // the least often reached for, so it does not belong in the middle of the
          // commission ones. My Event Activity is scoped to the caller by the API itself;
          // All Event Activity is Super Admin and Admin, enforced on the endpoint too.
          // (user 2026-10-03)
          { label: 'My Event Activity', path: '/reports/activity' },
          { label: 'All Event Activity', path: '/reports/activity/all', requiredRoles: ['Super Admin', 'Admin'] },
        ],
      },
    ],
  },
  {
    // Team — what an agent can see of the people around them. (user 2026-09-09)
    // Hierarchy stays on the AgentHierarchy right. My Team goes by role (user 2026-09-14):
    // the earning roles that recruit. Not Group Admin, Admin or Astern Admin, and not
    // Accountant or Referral. Keep this list in step with the route in App.js and
    // UserController.GetMyDownline.
    section: 'Team',
    items: [
      {
        label: 'Team',
        icon: GroupsIcon,
        children: [
          { label: 'Hierarchy', path: '/admin/hierarchy', requiredRoleRights: ['AgentHierarchy'] },
          { label: 'My Team',   path: '/team/my-team',    requiredRoles: ['Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          // The people THEY introduced. Under Team because it is a list of people;
          // what those people earned stays in Reports → My Referral Report, which
          // follows the sales. Same roles as My Team — anyone who can sell can refer.
          // (user 2026-10-20: "for referral is under team ... the page for own
          // referral can put referral")
          // Accountant is here for the same reason Super Admin is: they are the two
          // who get asked to fix a bank account or chase a missing IC, and the API
          // now shows them every referrer rather than the none that carry their own
          // upline id. (user 2026-10-22: "for the speradmin, and accounting how to
          //  help them create referral?")
          { label: 'Referral',  path: '/team/referrals',  requiredRoles: ['Super Admin', 'Accountant', 'Leader', 'Sales Agent', 'In-house Agent'] },
          // The introductions, beside the people who made them. Same roles: anyone who can
          // see a referrer can see what that referrer sent in, and the server narrows the
          // rows to their own. (user 2026-10-31)
          { label: 'Referral Leads', path: '/team/referral-leads', requiredRoles: ['Super Admin', 'Accountant', 'Leader', 'Sales Agent', 'In-house Agent'] },
        ],
      },
    ],
  },
  {
    section: 'Admin',
    items: [
      {
        label: 'Users',
        icon: PeopleIcon,
        children: [
          { label: 'All User', path: '/admin/users', requiredRoleRights: ['UserManagement'] },
          // Agent Applications consolidated into /approvals -> "Agent Applications" tab (v1.5.24)
          // Agent Hierarchy moved to the Team section (2026-09-09) — it is a team
          // view an agent uses, not user administration.
        ],
      },
      {
        label: 'Settings',
        icon: SettingsIcon,
        children: [
          { label: 'Profile', path: '/user/profile' },
          { label: 'Activity Logs', path: '/settings/logs', requiredRoleRights: ['UserManagement'] },
          { label: 'Quiz Questions', path: '/settings/quiz-questions', requiredRoleRights: ['UserManagement'] },
          // Permission Manager · Approver Config · Import Sales · Version Info now live
          // as tabs inside General Settings (Super Admin). Routes kept for direct access.
          { label: 'General Settings', path: '/settings/general', requiredRoles: ['Super Admin'] },
        ],
      },
    ],
  },
];

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const SideNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useUser();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openMenus, setOpenMenus] = useState({});
  const [activeDrawer, setActiveDrawer] = useState(null);

  const [deferredPrompt, setDeferredPrompt] = useState(window.__pwaInstallPrompt || null);

  const displayName = user?.firstName || user?.alias || '';
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];

  // Designer lockdown — true when the user carries ONLY the Designer role.
  // When true, every menu item except /design-requests is hidden.
  const isDesignerOnly =
    userRoles.includes('Designer') && userRoles.every(r => r === 'Designer');

  // Referral (and only Referral) sees the Commission menu only — no dashboard,
  // sales, hierarchy, resources, etc. Multi-role users keep their full access.
  const isReferralOnly =
    userRoles.includes('Referral') && userRoles.every(r => r === 'Referral');

  // Training room (user 2026-09-24) — an agent who has not passed the RSC
  // certification quiz sees Tutorials and the Quiz ONLY. It outranks every
  // other menu rule below, including Designer / Referral, because the route
  // guard and the API refuse everything else anyway; showing a link that
  // bounces to /quiz would only confuse. Exempt roles and admin-approved
  // users have canCreateSale = true and are unaffected.
  //
  // A pure Referral is NOT held back — the quiz examines the RSC calculator and they
  // never touch one. They keep canCreateSale = false, correctly, because they may not
  // create a sale; what changes is that this no longer doubles as "stuck in training".
  // The API agrees: AccessGate.IsTrainingExempt lets them through while the create-sale
  // gate still refuses them. (user 2026-10-31)
  const isTrainingOnly = user?.canCreateSale === false && !isReferralOnly;
  // /events is here on purpose: the weekly onboarding training is FOR the people
  // this gate holds back, so a new joiner who cannot see it can never attend the
  // session that certifies them. The API resolves each event's own audience, so
  // showing the link grants nothing beyond what they were invited to.
  // (user 2026-10-03)
  const TRAINING_PATHS = ['/tutorials', '/quiz', '/events'];

  // Commission availability — hides PSC/DOC/TOC/MOC menu links when the
  // current user has no historical earnings of that type. Defaults all to
  // FALSE while loading: the strict-history rule means a single misleading
  // chip is worse than a brief flicker. Menu fills in once the API resolves.
  const [commAvail, setCommAvail] = useState({ hasPsc: false, hasDoc: false, hasToc: false, hasMoc: false, hasMuc: false, hasRef: false, hasSop: false });
  useEffect(() => {
    if (!user?.userId) return;
    api.get('/CommissionProgress/GetMyCommAvailability')
      // Merged, not replaced: the SOP check below writes into the same object and either
      // call may land first.
      .then(r => setCommAvail(prev => ({ ...prev, ...(r.data || {}) })))
      .catch(() => { /* keep defaults; failure shouldn't break menu */ });
  }, [user?.userId]);

  // SOP availability — the right alone is not enough. The SOP walks through what happens
  // AFTER a sale exists, so an agent with no sale yet has nothing it is about and does not
  // see the link. The office is exempt; the API decides, and refuses the content by the
  // same rule, so this only keeps a dead link out of the menu. (user 2026-10-03)
  useEffect(() => {
    if (!user?.userId) return;
    api.get('/Sop/Access')
      .then(r => setCommAvail(prev => ({ ...prev, hasSop: r.data?.allowed === true })))
      .catch(() => { /* stays hidden; a link that only leads to a refusal is worse */ });
  }, [user?.userId]);

  // ---- PWA install prompt ----
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); window.__pwaInstallPrompt = e; setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    const prompt = deferredPrompt || window.__pwaInstallPrompt;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        setDeferredPrompt(null);
        window.__pwaInstallPrompt = null;
        if (outcome === 'accepted') {
          setTimeout(() => alert('App installed! Check your home screen.'), 300);
        }
      } catch (e) {
        console.log('Install prompt error:', e);
        showInstallGuide();
      }
    } else if (window.matchMedia('(display-mode: standalone)').matches) {
      alert('nexwave is already installed on this device.');
    } else {
      showInstallGuide();
    }
  };

  const showInstallGuide = () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS) {
      alert('To install:\n1. Tap the Share button (box with arrow)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add"');
    } else {
      alert('To install:\n1. Tap the browser menu (3 dots at top right)\n2. Tap "Add to Home screen" or "Install app"\n3. Tap "Install"');
    }
  };

  // ---- Responsive listener ----
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- Access control ----
  // excludedRoles wins over everything else — used to hide otherwise-
  // ungated items from restricted roles like Viewer.
  const hasAccess = (item) => {
    if (item.excludedRoles?.length) {
      const blocked = item.excludedRoles.some(role => user?.roles?.includes(role));
      if (blocked) return false;
    }
    if (item.requiredRoles?.length) {
      const ok = item.requiredRoles.some(role => user?.roles?.includes(role));
      if (!ok) return false;
    }
    if (!item.requiredRoleRights?.length) return true;
    return item.requiredRoleRights.some(r => user?.roleRights?.includes(r));
  };

  // Hide menu links whose availabilityKey resolves false in commAvail.
  // Items without an availabilityKey aren't gated.
  const isAvailable = (item) => {
    if (!item.availabilityKey) return true;
    return !!commAvail[item.availabilityKey];
  };

  const filterChildren = (children) => {
    if (isTrainingOnly) {
      return children.filter(c => TRAINING_PATHS.includes(c.path));
    }
    if (isDesignerOnly) {
      // Designer sees only the Design Request leaf; all other children are hidden.
      return children.filter(c => c.path === '/design-requests');
    }
    if (isReferralOnly) {
      // Referral earns only a referral fee — show just that link.
      return children.filter(c => c.path === '/commissions/referral' && hasAccess(c));
    }
    return children.filter(c => hasAccess(c) && isAvailable(c));
  };

  const hasVisibleChildren = (item) =>
    Array.isArray(item.children) && filterChildren(item.children).length > 0;

  const isItemVisible = (item) => {
    if (isTrainingOnly) {
      // Only the Resources group survives, and only its Tutorials + Quiz leaves.
      if (item.children) return hasVisibleChildren(item);
      return TRAINING_PATHS.includes(item.path);
    }
    if (isDesignerOnly) {
      // For parent groups (with children), show the group only if it still has
      // a visible child after the Designer filter (i.e. Pricing & Tools only).
      if (item.children) return hasVisibleChildren(item);
      // For leaf items, hide everything — the only visible leaf lives inside
      // the Pricing & Tools group and is handled by filterChildren above.
      return false;
    }
    if (isReferralOnly) {
      // The Commission group (the one with /commissions children), plus their own
      // dashboard — the one leaf a referrer has reason to open. (user 2026-10-18)
      if (item.children) return hasVisibleChildren(item);
      return item.path === '/referral/dashboard';
    }
    if (item.children) return hasAccess(item) && hasVisibleChildren(item);
    return hasAccess(item);
  };

  const isSectionVisible = (section) =>
    section.items.some(isItemVisible);

  // Flatten all visible top-level items (for mobile tabs)
  const allVisibleItems = (() => {
    const flat = NAV_SECTIONS
      .filter(isSectionVisible)
      .flatMap(s => s.items.filter(isItemVisible));
    // A trainee has exactly two places to be, and both live inside Resources -
    // so the phone showed ONE tab, "Resources", and an agent who never thought
    // to tap it reported that the quiz was all there was. Lift Tutorials and
    // the Quiz to the bar itself. (user 2026-09-27: "he told me just quiz only")
    if (!isTrainingOnly) return flat;
    const leaves = flat.flatMap(i => (i.children ? filterChildren(i.children) : []))
      .filter(c => TRAINING_PATHS.includes(c.path));
    return leaves.length ? leaves : flat;
  })();

  // ---- Menu toggle ----
  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // ---- Logout ----
  const handleLogout = () => {
    flushSync(() => {
      setUser(null);
      clearAuthStorage();
    });
    navigate('/');
  };

  // ---- Active check helpers ----
  const isPathActive = (path) => location.pathname === path;

  const isParentActive = (item) => {
    if (!item.children) return isPathActive(item.path);
    return filterChildren(item.children).some(c => isPathActive(c.path));
  };

  // ---- User initials & photo ----
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const profilePhotoUrl = user?.profilePhotoUrl;
  const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

  // ════════════════════════════════════════════
  //  DESKTOP SIDEBAR
  // ════════════════════════════════════════════

  const renderDesktop = () => (
    <aside className={`sidebar${isExpanded ? '' : ' collapsed'}`}>
      {/* Header */}
      <div className="sidebar-header" onClick={() => navigate(user?.roles?.length > 0 && user.roles.every(r => r === 'Viewer') ? '/user/profile' : isReferralOnly ? '/referral/dashboard' : '/dashboard')} style={{ cursor: 'pointer' }}>
        <img src="/nexwaveLogo.png" alt="nexwave" className="sidebar-logo" />
        <div className="sidebar-brand">
          <span className="sidebar-brand-name">nexwave</span>
          <span className="sidebar-brand-sub">Sales CRM</span>
        </div>
      </div>

      {/* Nav Body */}
      <div className="sidebar-body">
        {NAV_SECTIONS.map((section) => {
          if (!isSectionVisible(section)) return null;

          return (
            <div className="sidebar-section" key={section.section}>
              <div className="sidebar-section-label">{section.section}</div>

              {section.items.map((item) => {
                if (!isItemVisible(item)) return null;
                const Icon = item.icon || DashboardIcon;
                const isOpen = !!openMenus[item.label];

                // Leaf item (no children)
                if (!item.children) {
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      className={`sidebar-nav-item${isPathActive(item.path) ? ' active' : ''}`}
                      title={!isExpanded ? item.label : undefined}
                    >
                      <span className="nav-icon"><Icon sx={{ fontSize: 20 }} /></span>
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  );
                }

                // Parent item with children
                return (
                  <div key={item.label}>
                    <button
                      className={`sidebar-nav-item${isParentActive(item) ? ' active' : ''}`}
                      onClick={() => {
                        if (!isExpanded) {
                          setIsExpanded(true);
                          setOpenMenus(prev => ({ ...prev, [item.label]: true }));
                        } else {
                          toggleMenu(item.label);
                        }
                      }}
                      title={!isExpanded ? item.label : undefined}
                    >
                      <span className="nav-icon"><Icon sx={{ fontSize: 20 }} /></span>
                      <span className="nav-label">{item.label}</span>
                      <span className={`nav-arrow${isOpen ? ' open' : ''}`}>
                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                      </span>
                    </button>

                    <div className={`sidebar-submenu${isOpen ? ' open' : ''}`}>
                      {filterChildren(item.children).map((child) => (
                        child.action === 'installApp' ? (
                          <a key="installApp" className="sidebar-submenu-link" style={{ cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleInstallApp(); }}>
                            {child.label}
                          </a>
                        ) : child.externalUrl ? (
                          <a
                            key={child.label}
                            href={child.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sidebar-submenu-link"
                          >
                            {child.label}
                          </a>
                        ) : (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`sidebar-submenu-link${isPathActive(child.path) ? ' active' : ''}`}
                          >
                            {child.label}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar" onClick={() => navigate('/user/profile')} style={{ cursor: 'pointer' }}>
            {profilePhotoUrl ? (
              <img src={`${apiBase}${profilePhotoUrl}`} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : initials}
          </div>
          <div className="sidebar-user-info">
            <p className="sidebar-username">{displayName}</p>
            <p className="sidebar-userrole" title={userRoles.join(', ')}>{userRoles.join(', ')}</p>
          </div>
          <button
            className="sidebar-logout-btn"
            onClick={async () => {
              if ('serviceWorker' in navigator) {
                try {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map(r => r.unregister()));
                } catch { /* ignore */ }
              }
              window.location.reload();
            }}
            title="Refresh"
            style={{ marginRight: 4 }}
          >
            <RefreshIcon sx={{ fontSize: 20 }} />
          </button>
          <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">
            <LogoutIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <span style={{ fontSize: '0.6rem', color: '#475569', fontFamily: "'Poppins', sans-serif", opacity: 0.6 }}>v1.2.0</span>
        </div>
      </div>

      {/* Collapse toggle */}
      <button className="sidebar-collapse-btn" onClick={() => setIsExpanded(prev => !prev)}>
        <ChevronLeftIcon
          className={`collapse-icon${isExpanded ? '' : ' rotated'}`}
          sx={{ fontSize: 20 }}
        />
      </button>
    </aside>
  );

  // ════════════════════════════════════════════
  //  MOBILE BOTTOM NAV
  // ════════════════════════════════════════════

  const renderMobile = () => {
    const primaryTabs = allVisibleItems.slice(0, 4);
    const overflowItems = allVisibleItems.slice(4);
    const hasOverflow = overflowItems.length > 0;

    // Which remaining sections have overflow items
    const overflowSections = NAV_SECTIONS
      .map(section => ({
        ...section,
        items: section.items.filter(
          item => isItemVisible(item) && overflowItems.includes(item),
        ),
      }))
      .filter(section => section.items.length > 0);

    const closeDrawer = () => setActiveDrawer(null);

    const handleTabClick = (item) => {
      if (item.children) {
        setActiveDrawer(prev => prev === item.label ? null : item.label);
      } else {
        closeDrawer();
        navigate(item.path);
      }
    };

    return (
      <>
        {/* Bottom tab bar */}
        <nav className="mobile-bottom-nav">
          {primaryTabs.map((item) => {
            const Icon = item.icon || DashboardIcon;
            const active = isParentActive(item);
            return (
              <button
                key={item.label}
                className={`mobile-tab${active ? ' active' : ''}`}
                onClick={() => handleTabClick(item)}
              >
                <Icon className="mobile-tab-icon" sx={{ fontSize: 24 }} />
                <span className="mobile-tab-label">{item.label}</span>
              </button>
            );
          })}
          {hasOverflow && (
            <button
              className={`mobile-tab${activeDrawer === '__more__' ? ' active' : ''}`}
              onClick={() => setActiveDrawer(prev => prev === '__more__' ? null : '__more__')}
            >
              <GridViewIcon className="mobile-tab-icon" sx={{ fontSize: 24 }} />
              <span className="mobile-tab-label">More</span>
            </button>
          )}
        </nav>

        {/* Drawer backdrop */}
        {activeDrawer && (
          <div className="mobile-drawer-backdrop" onClick={closeDrawer} />
        )}

        {/* Item drawer (submenu) */}
        {activeDrawer && activeDrawer !== '__more__' && (() => {
          const item = allVisibleItems.find(i => i.label === activeDrawer);
          if (!item || !item.children) return null;
          const Icon = item.icon || DashboardIcon;
          const children = filterChildren(item.children);

          return (
            <div className="mobile-drawer">
              <div className="mobile-drawer-header">
                <h3 className="mobile-drawer-title">{item.label}</h3>
                <button className="mobile-drawer-close" onClick={closeDrawer}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </button>
              </div>
              <div className="mobile-drawer-body">
                {children.map(child => (
                  child.action === 'installApp' ? (
                    <a key="installApp" className="mobile-drawer-link" style={{ cursor: 'pointer' }} onClick={async (e) => { e.preventDefault(); e.stopPropagation(); await handleInstallApp(); closeDrawer(); }}>
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </a>
                  ) : child.externalUrl ? (
                    <a
                      key={child.label}
                      href={child.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mobile-drawer-link"
                      onClick={closeDrawer}
                    >
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </a>
                  ) : (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`mobile-drawer-link${isPathActive(child.path) ? ' active' : ''}`}
                      onClick={closeDrawer}
                    >
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </Link>
                  )
                ))}
              </div>
            </div>
          );
        })()}

        {/* More drawer */}
        {activeDrawer === '__more__' && (
          <div className="mobile-drawer">
            <div className="mobile-drawer-header">
              <h3 className="mobile-drawer-title">More</h3>
              <button className="mobile-drawer-close" onClick={closeDrawer}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
            <div className="mobile-drawer-body">
              {overflowSections.map(section => (
                <div className="more-drawer-section" key={section.section}>
                  <div className="more-drawer-section-label">{section.section}</div>
                  {section.items.map(item => {
                    const Icon = item.icon || DashboardIcon;

                    // Leaf item — same as before.
                    if (!item.children) {
                      return (
                        <Link
                          key={item.label}
                          to={item.path}
                          className={`mobile-drawer-link${isPathActive(item.path) ? ' active' : ''}`}
                          onClick={closeDrawer}
                        >
                          <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                          {item.label}
                        </Link>
                      );
                    }

                    // Parent item — render as expandable group, mirroring desktop sidebar.
                    // Children are no longer flattened directly under the SECTION header; they sit
                    // under the parent's label (e.g. "Pricing & Tools" / "Resources") so structure
                    // matches the desktop.
                    const isOpen = !!openMenus[item.label];
                    return (
                      <div key={item.label}>
                        <button
                          type="button"
                          className={`mobile-drawer-link mobile-drawer-parent${isParentActive(item) ? ' active' : ''}`}
                          onClick={() => toggleMenu(item.label)}
                        >
                          <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                          <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                          <ExpandMoreIcon
                            className={`mobile-drawer-parent-arrow${isOpen ? ' open' : ''}`}
                            sx={{ fontSize: 18 }}
                          />
                        </button>
                        <div className={`mobile-drawer-submenu${isOpen ? ' open' : ''}`}>
                          {filterChildren(item.children).map(child => (
                            child.externalUrl ? (
                              <a
                                key={child.label}
                                href={child.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-drawer-link mobile-drawer-child"
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`mobile-drawer-link mobile-drawer-child${isPathActive(child.path) ? ' active' : ''}`}
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div className="more-drawer-divider" />

              {/* User info */}
              <div className="more-drawer-user">
                <div className="more-drawer-avatar" onClick={() => navigate('/user/profile')} style={{ cursor: 'pointer' }}>
                  {profilePhotoUrl ? (
                    <img src={`${apiBase}${profilePhotoUrl}`} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : initials}
                </div>
                <div>
                  <p className="more-drawer-username">{displayName}</p>
                  <p className="more-drawer-role">{userRoles.join(', ')}</p>
                </div>
              </div>

              <button className="more-drawer-logout" onClick={handleLogout}>
                <LogoutIcon sx={{ fontSize: 20 }} />
                Logout
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════

  return isMobile ? renderMobile() : renderDesktop();
};
